import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "./button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./dialog";

// =============================================================================
// ConfirmDialog
//
// Replacement for window.confirm() / window.alert() that ships an
// in-page Radix dialog instead of the system prompt. Mounted once at
// the app root via <ConfirmDialogProvider>; consumers grab the
// imperative `useConfirm()` hook and call it like:
//
//   const confirm = useConfirm();
//   const ok = await confirm({
//     title: "Sign out of every other device?",
//     description: "Other browsers / phones will need to sign in again.",
//     confirmText: "Sign out everywhere",
//     destructive: true,
//   });
//   if (!ok) return;
//
// Setting `cancelText: null` switches the dialog into single-button
// "alert" mode, which is the friendlier replacement for window.alert.
// =============================================================================

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmText?: string;
  /** Pass null to render only the confirm button (alert-style). */
  cancelText?: string | null;
  /** Render the confirm button in danger styling. */
  destructive?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  // Track whether the active dialog has already been resolved so the
  // onOpenChange handler doesn't double-resolve when closing.
  const resolvedRef = useRef(false);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        resolvedRef.current = false;
        setPending({ options, resolve });
      }),
    [],
  );

  const settle = useCallback(
    (value: boolean) => {
      if (resolvedRef.current) return;
      resolvedRef.current = true;
      pending?.resolve(value);
      setPending(null);
    },
    [pending],
  );

  // Esc / overlay-click → treat as cancel. The Radix Dialog fires
  // onOpenChange(false) for both, so we just resolve(false) there.
  const onOpenChange = useCallback(
    (next: boolean) => {
      if (!next) settle(false);
    },
    [settle],
  );

  // Defensive: if the provider unmounts mid-prompt, resolve any
  // still-pending dialog as cancelled so the awaiting caller doesn't
  // hang forever.
  useEffect(() => {
    return () => {
      if (pending && !resolvedRef.current) {
        resolvedRef.current = true;
        pending.resolve(false);
      }
    };
  }, [pending]);

  const open = pending !== null;
  const opts = pending?.options;
  const isAlert = opts?.cancelText === null;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          hideCloseButton
          structured
          // Alert-style (single-button) prompts get role="alertdialog" for the
          // more urgent screen-reader announcement window.alert() used to give.
          // Confirmation flows fall back to Radix's default role="dialog" — so
          // we OMIT the prop entirely rather than pass `undefined`, which would
          // override that default and leave the dialog with no role at all.
          {...(isAlert ? { role: "alertdialog" as const } : {})}
          className="max-w-[480px]"
        >
          {opts ? (
            <>
              <DialogHeader
                icon={AlertTriangle}
                tone={opts.destructive ? "danger" : "primary"}
                className="pb-6"
              >
                <DialogTitle>{opts.title}</DialogTitle>
                {opts.description ? (
                  <DialogDescription>{opts.description}</DialogDescription>
                ) : null}
              </DialogHeader>
              <DialogFooter>
                {!isAlert ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => settle(false)}
                  >
                    {opts.cancelText ?? "Cancel"}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant={opts.destructive ? "destructive" : "primary"}
                  onClick={() => settle(true)}
                  autoFocus
                >
                  {opts.confirmText ?? (isAlert ? "OK" : "Confirm")}
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used inside <ConfirmDialogProvider>");
  }
  return ctx;
}
